import { useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { getRegItems } from 'registry/reducers'
import { InfoButton } from 'components/buttons'
import { ProductPreviewCard } from 'components/card'
import { formatStoreNameToIconName } from 'components/icons'
import { setActiveModal } from 'registry/actions'
import { PRODUCT_OFFERS } from 'registry/constants'
import { SOURCE_FEED } from 'shared/constants'
import { track, tracking } from 'lib/analytics'
import css from './feed-items.scss'
import { SectionHeading } from '../../../../components/headings'
import classNames from 'classnames'

const ProductShape = {
  id: PropTypes.number,
  name: PropTypes.string,
  image: PropTypes.shape({
    thumbnailUrl: PropTypes.string,
    url: PropTypes.string,
  }),
  rating: PropTypes.number,
  review_count: PropTypes.number,
  brand: PropTypes.string,
  offers: PropTypes.arrayOf(
    PropTypes.shape({
      price: PropTypes.number,
      store: PropTypes.string,
      storeName: PropTypes.string,
      url: PropTypes.string,
    })
  ),
}

const ProductCard = ({
  isFeaturedProduct,
  product,
  productOnRegistry,
  setActiveModal,
  productId,
  regItemId,
}) => {
  const hasMultipleOffers = product.offers?.length > 1

  const openProductOffersModal = () => {
    const { brand, image, name, offers } = product
    setActiveModal(PRODUCT_OFFERS, {
      brand,
      imageUrl: image.thumbnailUrl,
      name,
      offers,
      productId,
      regItemId,
    })
  }

  const handleProductLinkClick = (e) => {
    if (hasMultipleOffers) {
      e.preventDefault() // Don't navigate to link
      openProductOffersModal()
    }
  }

  const storeOfferIconNames = product.offers.map((o) =>
    formatStoreNameToIconName(o.storeName)
  )

  // redefine the event context to track product click
  const ProductPreviewCardWithContextualizedTracking = tracking({
    eventType: track.EventType.PRODUCT_CLICK,
    productIds: [product.id],
    product,
    events: [track.productClicked],
  })(ProductPreviewCard)

  return (
    <ProductPreviewCardWithContextualizedTracking
      key={product.id}
      imageUrl={product.image.thumbnailUrl}
      productUrl={product.offers[0].affiliateUrl}
      brand={product.brand}
      name={product.name}
      price={{
        min: product.price.min,
        max: product.price.max,
      }}
      rating={product.rating}
      reviewCount={product.reviewCount}
      onProductLinkClick={handleProductLinkClick}
      storeOfferIconNames={storeOfferIconNames}
      onStoreOffersClick={openProductOffersModal}
      QuickAddButtonComponent={
        <ProductPreviewCard.QuickAddRegItem
          productOnRegistry={productOnRegistry}
          product={product}
          source={SOURCE_FEED}
        />
      }
      size={isFeaturedProduct ? 'large' : 'small'}
      layout={isFeaturedProduct ? 'horizontal' : 'vertical'}
      __forcePageLoad
      openInNewWindow
    />
  )
}

ProductCard.propTypes = {
  isFeaturedProduct: PropTypes.bool.isRequired,
  product: PropTypes.shape(ProductShape).isRequired,
  productId: PropTypes.number,
  productOnRegistry: PropTypes.bool.isRequired,
  regItemId: PropTypes.number,
  setActiveModal: PropTypes.func.isRequired,
}

ProductCard.defaultProps = {
  productId: undefined,
  regItemId: undefined,
}

const Products = (
  {
    productIdsOnRegistry,
    products,
    setActiveModal,
    teaser,
    teaserLinkText,
    teaserLinkUrl,
    title,
  },
  context
) => {
  const { isMobile } = context
  const [showAllProducts, setShowAllProducts] = useState(false)
  const productsWithOffers = products.filter((p) => p.offers.length > 0)
  const maxVisibleProducts = isMobile ? 4 : 8
  const productsToDisplay = showAllProducts
    ? productsWithOffers
    : productsWithOffers.slice(0, maxVisibleProducts)
  const isFeaturedProduct = products && products.length === 1

  // If all products are out of stock, the api will return no products
  // so don't show this feed item if that's the case
  if (productsWithOffers.length === 0) {
    return null
  }

  return (
    <div className="mbxl">
      <SectionHeading className="h2 text-bold mll">{title}</SectionHeading>
      <p className="pbm mll">
        {teaser}
        {teaserLinkText && teaserLinkUrl && (
          <a href={teaserLinkUrl} className={classNames('mls', css.teaserLink)}>
            {teaserLinkText}
          </a>
        )}
      </p>

      <ProductCardLayout isFeaturedProduct={isFeaturedProduct}>
        {productsToDisplay.map((product) => {
          return (
            <ProductCard
              key={product.id}
              product={product}
              isFeaturedProduct={isFeaturedProduct}
              productOnRegistry={productIdsOnRegistry.some(
                (p) => p.productId === product.id
              )}
              setActiveModal={setActiveModal}
            />
          )
        })}
      </ProductCardLayout>

      {productsWithOffers.length > maxVisibleProducts && (
        <div className="text-center">
          <InfoButton
            onClick={() => setShowAllProducts(!showAllProducts)}
            className="text-center mtl"
            pill
            empty
            size="small"
          >
            {!showAllProducts
              ? `+ ${productsWithOffers.length - maxVisibleProducts} More`
              : `View Less`}
          </InfoButton>
        </div>
      )}
    </div>
  )
}

Products.propTypes = {
  productIdsOnRegistry: PropTypes.arrayOf(PropTypes.number),
  products: PropTypes.arrayOf(PropTypes.shape(ProductShape)).isRequired,
  setActiveModal: PropTypes.func,
  teaser: PropTypes.string.isRequired,
  teaserLinkText: PropTypes.string,
  teaserLinkUrl: PropTypes.string,
  title: PropTypes.string.isRequired,
}

Products.defaultProps = {
  productIdsOnRegistry: [],
  teaserLinkText: undefined,
  teaserLinkUrl: undefined,
  setActiveModal: undefined,
}

Products.contextTypes = {
  isMobile: PropTypes.bool.isRequired,
}

const ProductCardLayout = ({ children, isFeaturedProduct = false }) => (
  <div
    className={isFeaturedProduct ? css.featuredProductLayout : css.sliderLayout}
  >
    {children}
  </div>
)

ProductCardLayout.propTypes = {
  children: PropTypes.node.isRequired,
  isFeaturedProduct: PropTypes.bool.isRequired,
}

const mapStateToProps = (state) => ({
  productIdsOnRegistry: getRegItems(state).reduce((ids, regItem) => {
    if (regItem.productId) {
      ids.push({ productId: regItem.productId })
    }
    return ids
  }, []),
})

export default connect(mapStateToProps, {
  setActiveModal,
})(Products)
